import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { add } from 'date-fns'
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup'
import produce from 'immer';
import { useQuery } from 'react-query'
import Select from 'react-select';
import * as Yup from 'yup';
import clsx from 'clsx';
import {
  Button,
  Media,
  Collapse
} from 'reactstrap';


import { fetcher } from 'api/client'
import { fetchMembers } from 'api/actions/collaboration'
import UpgradePlan from 'components/Shared/UpgradePlan'
import DatePicker from 'components/Shared/DatePicker'
import { useEditorState, useEditorActions } from '../../editor-context'
import { useProfileState, useProfileAbility } from 'context/profile'
import { range, zeroPad, truncate } from 'utils'


export function Share() {
    const {t} = useTranslation()
    const profileAbility = useProfileAbility();
    const canShareTemplate = profileAbility.can('SHARE_TEMPLATE'); // can SHARE_TEMPLATE
    const { document: doc, mode } = useEditorState();
    const { saveDocument, updateDocument } = useEditorActions();
    const [watchers, setWatchers] = useState(doc.watchers);
    const { data, isLoading } = useQuery(['members', { documentId: doc._id, documentType: mode  }], () => fetcher(fetchMembers({ documentId: doc._id, documentType: mode  })))

    useEffect(() => {
      setWatchers(doc.watchers)
    }, [doc.watchers])

  
    const handleShare = (memberOrGroup, action) => {
      if (isLoading) return;

      const newWatchers = produce(watchers, draft => {
        if (action === 'share-all') {
  
          const allMembers = data?.reduce((array, group) => {
              const groupMembers = group?.members?.filter(m => m._id && doc.user?._id && doc.user?._id !== m._id).map(m => m._id) ?? [];

              return [...new Set([...array ,...groupMembers])];
          }, []).flat();

          return allMembers;  
        } else if (action === 'share-group') {
          const group = memberOrGroup.members?.filter(m => m._id && doc.user?._id && doc.user?._id !== m._id).map(m => m._id).filter(m => !draft.includes(m))
          return [...new Set([...draft ,...group])];
        } else if (action === 'share') {
          draft.push(memberOrGroup._id)
        } else {
          const idx = draft.indexOf(memberOrGroup._id);
          draft.splice(idx, 1); 
        }
      });

      setWatchers(newWatchers)
      updateDocument({ watchers: newWatchers })
    }
  
    if (!canShareTemplate) {
      return <UpgradePlan inverted />
    }


    return (  
        <div className="sidebar-content text-center">
            <Button color="light" className="sidebar-share-all m-1" onClick={() => handleShare(null, 'share-all')}>
              <i className="fas fa-users"></i> {t('editor.share.share_all')}
            </Button>
            <div className="sidebar-content-members">
              {
                data?.map(member => (
                  <MemberItem member={member} owner={doc.user._id} watchers={watchers} onShare={handleShare} />
                ))
              }
            </div>
        </div>
    )
}
function MemberItem({ member, owner, watchers, onShare }) {
  const {t} = useTranslation()
  const { group, photo, name, givenName, familyName, members = [], admin, email  } = member
  const { profile: { tenant } } = useProfileState()
  const [isOpen, setIsOpen] = useState(false)

  const toggle = () => setIsOpen(open => !open)

  const handleShare = (member, action) => () => {
    onShare(member, action)
  }


  return (
    <div className="member-item">
      <Media className="member-wrapper p-2 d-flex align-items-center border">
        <Media className="member-photo">
          {group ?
            <i className="rounded-circle border-secondary-light fas fa-users" src="/images/editor/icons/group.png" style={{width: 35}} />
          : photo ?
            <img className="rounded-circle border-secondary-light" src={`${photo}`}  width="35px" />
          :
            <span>{truncate(givenName).toLowerCase()}{truncate(familyName).toLowerCase()}</span>
          }
        </Media>
        <Media className="member-info text-dark" body>
          {group ? <React.Fragment>
            <span className="member-name" data-email="{{adminEmail}}">{ tenant.plan === 'ABOGADO' && !admin ? 'Clientes' : name }</span>
            <span className="member-email">{t('editor.collaboration.group_label')} - {members.length} {t('editor.collaboration.group_members')}</span>
          </React.Fragment> : 
          <React.Fragment>
            <span className="member-name" data-email="{{email}}">{givenName} {familyName}</span>
            <span className="member-email">{email}</span>
          </React.Fragment>
          }
        </Media>
    
      <Media className="member-caret" onClick={toggle}>
        <Button color="light" href="#">
            <i className={`fas fa-chevron-${isOpen ? 'down' : 'up'}`}></i>
        </Button>
      </Media>
    </Media>
    <Collapse isOpen={isOpen}>
      <div className={clsx('member-share-configuration bg-light-light')}>
        {members && <SimpleMemberItem member={member} members={members} owner={owner} watchers={watchers} onShare={handleShare} /> }
      </div>
    </Collapse>
  
    </div>
  )
}




function SimpleMemberItem({member, members, owner, watchers, onShare}) {
  const { t } = useTranslation();
  const isOwner = member._id === owner;

  const getButtonProps = useCallback(
    ({member}) => {
      const isOwner = member._id === owner;
      if (isOwner) {
        return {
          text: 'editor.share.share_owner',
          color: 'primary',
          isDisabled: true,
          action: ''
        }
      }
      if (watchers.includes(member._id)) {
        return {
          text: 'editor.share.shared_action',
          color: 'success',
          action: 'unshare'
        }
      }
      return {
        text: 'editor.share.share_action',
        color: 'light',
        action: 'share'
      }
    },
    [owner, watchers],
  )
  


  return (
    <div className="p-2">
      <div className="row p-2">
        <span className="text-dark col-6">{t('editor.share.share_group')}</span>
        <div className="col-6">
          <Button  size="md" color="info" className="share-document" data-action="share-group" onClick={onShare(member, 'share-group')}>
            {t('editor.share.share_action')}
          </Button>
        </div>

      </div>
      <div>
        {members.map(member => {
          const buttonProps = getButtonProps({ member }); 
          return (
            <div className="g-member p-2 row" data-email="{{email}}">
              <span className="text-dark col-6">
                {member.givenName} {member.familyName}
              </span>
              <div className="text-center col-6">
                <Button size="smd" color={buttonProps.color} disabled={buttonProps.isDisabled} onClick={onShare(member, buttonProps.action)}>
                  {t(`${buttonProps.text}`)}
                </Button>
              </div>
            </div>
          )})}
      </div>
    </div>
  )
}